import { ISession } from "@augloop/runtime-client";
import { ErrorStringFromException, isEmptyString, isNullOrUndefined } from "../../../utils/utils";
import { EnsureAugloopRuntimeManager, waitForAugloopConnectionToBeConnected } from "../../augloop/Augloop";
import { IdentityType, PrimaryIdentityInfo } from "../../interfaces/IAuthenticationProvider";
import { TokenProvisionMessage } from "@augloop/session-protocol";
import { GetAugloopResourceUrl } from "../../augloop/AugloopConfig";
import { LogError } from "../../../utils/ErrorTelemetry";
import { LogEventNames } from "../../interfaces/ITelemetryLogger";
import { IAuthenticationProvider } from "../../interfaces/IAuthenticationProvider";
import { HostAppSettingName, HostAppSettings } from "../HostAppSettings";
import { HOST_PLATFORM_WIN32 } from "../../../AppConstants";
import { HostAppUtil } from "../HostAppUtil";

export async function GetPrimaryIdentity() {
  return await GetPrimaryIdentityInfo();
}

export function IsPrimaryIdentityInformationValid(primaryIdentity: PrimaryIdentityInfo | null) {
  if (!isNullOrUndefined(primaryIdentity) && (primaryIdentity?.displayName != undefined && !isEmptyString(primaryIdentity?.displayName))
    && (primaryIdentity?.email != undefined && !isEmptyString(primaryIdentity?.email))) {
    return true;
  }
  return false;
}

export async function GetPrimaryIdentityInfo(): Promise<PrimaryIdentityInfo> {
  //Note: getPrimaryIdentityInfo is called to get the primary identity type of the accounts logged in.
  const primaryIdentityValue = await OfficeFirstPartyAuth.getPrimaryIdentityInfo();
  const identityType = primaryIdentityValue.identityType;

  switch (identityType) {
      case IdentityType.ODP:
          return { type: IdentityType.ODP, displayName : primaryIdentityValue.displayName, email : primaryIdentityValue.email };
      case IdentityType.ODB:
          return { type: IdentityType.ODB, displayName : primaryIdentityValue.displayName, email : primaryIdentityValue.email };
      default:
          return { type: IdentityType.Unsupported, displayName : primaryIdentityValue.displayName, email : primaryIdentityValue.email };
  }
}

export async function ProvisionAuthenticationToken(authProvider: IAuthenticationProvider): Promise<boolean> {
  try {
    // Bypass token provisioning for licensed consumer (MSA) users on Win32 platform only.
    // Web platform MSA users need to provision token for proper transcription service access.
    // Once the issue for getting consumer's token on Win32 is fixed, we should remove this logic.
    if (HostAppSettings.IsSettingTrue(HostAppSettingName.IsLicensedUser) && HostAppUtil.GetHostAppPlatform() === HOST_PLATFORM_WIN32) {
      return true;
    }

    if (IsTokenProvisioned()) {
      return true;
    }
    
    const alRuntimeManager = await EnsureAugloopRuntimeManager();
    await waitForAugloopConnectionToBeConnected();

    const token = await authProvider.GetAccessToken(GetAugloopResourceUrl(authProvider.GetAuthEnvironment()));
    const session: ISession = await alRuntimeManager.getSession();
    await session.submitCustomMessage(new TokenProvisionMessage({ authToken: token }))
    HostAppSettings.Set(HostAppSettingName.IsTokenProvisioned, true);
    return true;
  } catch (error) {
    LogError(LogEventNames.Boot, "Failed to get token: " + ErrorStringFromException(error));
    // Bypass the issue if get token failed since token is not required for CTS
    return false;
  }
}

function IsTokenProvisioned(): boolean {
  return HostAppSettings.GetSetting(HostAppSettingName.IsTokenProvisioned) ?? false;
}